import { useState, useEffect } from "react";
import { useCustomLocaleText } from "@/Components/Common/useCustomLocaleText";
import {
  Box,
  Typography,
  Button,
  TextField,
  useTheme,
  ClickAwayListener,
} from "@mui/material";
import { DataGridPro, GridColDef, GridOverlay } from "@mui/x-data-grid-pro";
import Pagination from "@/Components/Common/TablePro/Pagination";
import {
  KeyboardArrowDown,
  KeyboardArrowRight,
  Add,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { AnyObject } from "yup";
import TableAction from "@/Components/Common/TablePro/TableAction";
import NestedTaskList from "./NestedTaskList";
import { LoadingButton } from "@mui/lab";
import {
  useCreateStatusMutation,
  useDeleteStatusMutation,
  useUpdateStatusMutation,
} from "@/features/bughurd/api";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import DeleteConfirmModal from "@/Components/Common/DeleteConfirmModal";
import { Icon } from "@iconify/react";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import {
  selectColumns,
  selectColumnOrder,
  selectLoadingStates,
  selectGroupingConfig,
  selectFilters,
  selectApiData,
  selectBoardState,
  selectActiveSprintIds,
  selectSearchQuery,
} from "@/features/bughurd/boardSelector";
import {
  setColumns,
  setColumnOrder,
  updateColumn,
  setIsAddingColumn,
  setNewColumnTitle,
  setDeleteConfirmModal,
  resetBoardState,
  setSelectedItem,
} from "@/features/bughurd/slice";
import DEFEKT_TRACKER from "@/app/permissionNew/defectTracker";
import { useColumnOperations } from "./custom-hooks/useColumnOperations";

type ListViewProps = {
  handlePageChange: (
    columnId: string | null,
    paginationModel: any,
    viewType: string
  ) => void;
  refreshColumnData: (columnId?: string) => void;
  isMyTaskView: boolean;
  isInitialLoading: boolean;
};

// Fake row ID for the "Add Status" row
const ADD_STATUS_ROW_ID = "add-status-row";
// Fake row ID for the "Start Sprint" row
const START_SPRINT_ROW_ID = "start-sprint-row";

const ListView = ({
  handlePageChange,
  refreshColumnData,
  isMyTaskView,
  isInitialLoading,
}: ListViewProps) => {
  const { roleCodeDetails: myPermissions } = useAppSelector(
    (state) => state.appGlobal
  );

  const customLocaleText = useCustomLocaleText();
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { handleColumnTitleChange } = useColumnOperations(id, isMyTaskView);
  // Redux selectors
  const columns = useAppSelector(selectColumns);
  const columnOrder = useAppSelector(selectColumnOrder);
  const loadingStates = useAppSelector(selectLoadingStates);
  const { groupBy } = useAppSelector(selectGroupingConfig);
  const searchQuery = useAppSelector(selectSearchQuery);
  const {
    selectedStatuses,
    selectedAvatars,
    selectedPriorities,
    selectedTags,
    selectedProjects,
    dueDateStart,
    dueDateEnd,
    startDateStart,
    startDateEnd,
    updatedAtStart,
    updatedAtEnd,
    isRecurring,
    selectedCreatedBy,
  } = useAppSelector(selectFilters);
  const boardState = useAppSelector(selectBoardState);

  // Check if any filters are active
  const hasFilters =
    (searchQuery && searchQuery.trim().length > 0) ||
    selectedAvatars?.length > 0 ||
    selectedTags?.length > 0 ||
    selectedStatuses?.length > 0 ||
    selectedPriorities?.length > 0 ||
    selectedProjects?.length > 0 ||
    selectedCreatedBy?.length > 0 ||
    dueDateStart ||
    dueDateEnd ||
    startDateStart ||
    startDateEnd ||
    updatedAtStart ||
    updatedAtEnd ||
    isRecurring;
  const {
    pagination,
    isAddingColumn,
    newColumnTitle,
    isDeleteConfirmOpen,
    columnToDelete,
    projectDetails,
  } = boardState;

  const boardType = projectDetails?.boardType;
  const activeSprintIds = useAppSelector(selectActiveSprintIds);

  const [expandedDetailPanels, setExpandedDetailPanels] = useState<string[]>(
    []
  );
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });

  // For delete functionality
  const [statusToDelete, setStatusToDelete] = useState<any>(null);
  const [deleteStatus, { isLoading: isDeletingStatus }] =
    useDeleteStatusMutation();

  // For rename functionality
  const [editingStatusId, setEditingStatusId] = useState<string | null>(null);
  const [editedStatusTitle, setEditedStatusTitle] = useState("");
  const [updateStatus, { isLoading: isUpdatingStatus }] =
    useUpdateStatusMutation();

  const [createStatus, { isLoading: isCreatingStatus }] =
    useCreateStatusMutation();

  const shouldShowDetailPanel = (rowId: string) => {
    // Don't show detail panel for special rows
    return rowId !== ADD_STATUS_ROW_ID && rowId !== START_SPRINT_ROW_ID;
  };

  const handleAction = (data: any, action: string, type: string) => {
    if (action === "delete" && type === "parent") {
      // Handle status deletion
      setStatusToDelete({
        id: data?.id,
        status: data?.title,
      });
      dispatch(
        setDeleteConfirmModal({
          open: true,
          column: {
            id: data?.id,
            title: data?.title,
          },
        })
      );
    } else if (action === "rename" && type === "parent") {
      // Start rename process
      setEditingStatusId(data.id);
      setEditedStatusTitle(data.title);
    } else if (action === "add" && type === "parent") {
      // Handle add task to this status
    }
  };

  // Handle status deletion
  const handleDeleteStatus = async () => {
    if (!columnToDelete) return;
    try {
      await deleteStatus(columnToDelete.id)
        .unwrap()
        .then(() => {
          toast.success(
            t(`Status "${columnToDelete.title}" deleted successfully`)
          );
          dispatch(
            setDeleteConfirmModal({
              open: false,
              column: null,
            })
          );
          // Refresh data after deletion
          refreshColumnData();
        })
        .catch((error) => {
          console.error("Delete error:", error);
          toast.error(
            t(
              `Failed to delete status: ${
                error?.data?.message || t("Unknown error")
              }`
            )
          );
        });
    } catch (error) {
      console.error("Error deleting status:", error);
      toast.error(t("An unexpected error occurred while deleting status"));
    }
  };

  // Handle status rename submission
  const handleRenameSubmit = async () => {
    if (!editedStatusTitle.trim() || !editingStatusId) {
      setEditingStatusId(null);
      return;
    }

    const originalStatus = allRows.find((row) => row.id === editingStatusId);
    if (!originalStatus || originalStatus.title === editedStatusTitle) {
      setEditingStatusId(null);
      return;
    }

    try {
      handleColumnTitleChange(editingStatusId, editedStatusTitle);
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error(t("An unexpected error occurred"));
    } finally {
      setEditingStatusId(null);
    }
  };

  // Handle clicking on status title to start editing
  const handleTitleClick = (statusId: string, currentTitle: string) => {
    if (
      statusId === ADD_STATUS_ROW_ID ||
      statusId === START_SPRINT_ROW_ID ||
      isMyTaskView
    )
      return;

    setEditingStatusId(statusId);
    setEditedStatusTitle(currentTitle);
  };

  // Handle go to backlog button click
  const handleGoToBacklog = () => {
    dispatch(setSelectedItem("Backlog"));
    navigate(`/defect-tracker/projects/${id}/backlog`);
  };

  const addStatus = async () => {
    if (!newColumnTitle.trim()) return;

    const payload = {
      status: newColumnTitle,
      project: id || "",
    };

    try {
      const res = await createStatus(payload).unwrap();
      if (res?.data?.decryptedData?._id) {
        dispatch(setNewColumnTitle(""));
        dispatch(setIsAddingColumn(false));
        toast.success(t("Status added successfully"));

        // Add the new column to Redux state
        const newColumnId = res.data.decryptedData._id;
        const newColumn = {
          id: newColumnId,
          title: res.data.decryptedData.status,
          color: res.data.decryptedData.color,
          icon: res.data.decryptedData.icon,
          tasks: [],
          totalCount: 0,
          description: res.data.decryptedData.description || "",
          hidden: res.data.decryptedData.hidden || false,
        };

        dispatch(
          updateColumn({
            columnId: newColumnId,
            column: newColumn,
          })
        );

        dispatch(setColumnOrder([...columnOrder, newColumnId]));

        // Refresh data to get tasks for the new status
        refreshColumnData(newColumnId);
      } else {
        toast.error(t("Failed to add status"));
      }
    } catch (err: any) {
      toast.error(err?.data?.message || t("Failed to add status"));
    }
  };

  // Handle key down events for the TextField
  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();

    if (e.key === "Enter") {
      addStatus();
    } else if (e.key === "Escape") {
      dispatch(setIsAddingColumn(false));
      dispatch(setNewColumnTitle(""));
    }
  };

  // Handle key press for status rename field
  const handleRenameKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleRenameSubmit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setEditingStatusId(null);
      setEditedStatusTitle("");
    }
  };

  // Handle change events for the TextField
  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setNewColumnTitle(e.target.value));
  };

  // Handle change events for rename TextField
  const handleRenameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedStatusTitle(e.target.value);
  };

  const handleDeleteConfirmClose = () => {
    dispatch(
      setDeleteConfirmModal({
        open: false,
        column: null,
      })
    );
  };

  const listColumns: GridColDef[] = [
    {
      field: "title",
      headerName: t(`Group: ${groupBy}`),
      flex: 2,
      resizable: false,
      renderCell: (params) => {
        // Special rendering for the "Start Sprint" row
        if (params.id === START_SPRINT_ROW_ID) {
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                height: "100%",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                  height: "100%",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ fontWeight: "bold" }}
                >
                  {t(
                    "Get started in backlog. Plan and start a sprint to see work here."
                  )}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                onClick={(e) => {
                  e.stopPropagation();
                  handleGoToBacklog();
                }}
                size="medium"
                sx={{
                  textTransform: "none",
                  fontSize: "14px",
                  mx: 1,
                }}
              >
                {t("Go to backlog")}
              </Button>
            </Box>
          );
        }

        // Special rendering for the "Add Status" row
        if (params.id === ADD_STATUS_ROW_ID) {
          if (!isAddingColumn) {
            return (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  dispatch(setIsAddingColumn(true));
                }}
                sx={{
                  height: "fit-content",
                  backgroundColor: (theme) => theme.palette.action.hover,
                  color: "text.secondary",
                  textTransform: "none",
                  fontSize: "13px",
                  minHeight: "27px",
                  p: 1,
                  justifyContent: "flex-start",
                  "&:hover": {
                    backgroundColor: (theme) => theme.palette.action.selected,
                  },
                }}
                startIcon={<Add />}
                disabled={
                  !DEFEKT_TRACKER.CREATE(myPermissions) ||
                  !DEFEKT_TRACKER.UPDATE(myPermissions)
                }
              >
                {t("Add new status group")}
              </Button>
            );
          } else {
            return (
              <ClickAwayListener
                onClickAway={() => {
                  dispatch(setIsAddingColumn(false));
                  dispatch(setNewColumnTitle(""));
                }}
              >
                <Box
                  sx={{
                    backgroundColor: (theme) => theme.palette.background.paper,
                    borderRadius: 1,
                    p: 1,
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <TextField
                    size="small"
                    value={newColumnTitle}
                    onChange={handleTextChange}
                    onKeyDown={handleKeyDown}
                    placeholder={t("Enter status name...")}
                    autoFocus
                    inputProps={{
                      onKeyDown: (e) => e.stopPropagation(),
                      onClick: (e) => e.stopPropagation(),
                    }}
                    sx={{
                      "& .MuiInputBase-input": {
                        paddingLeft: 1,
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                    }}
                  />
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <LoadingButton
                      variant="contained"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        addStatus();
                      }}
                      loading={isCreatingStatus}
                    >
                      {t("Add Status")}
                    </LoadingButton>
                    <Button
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        dispatch(setIsAddingColumn(false));
                        dispatch(setNewColumnTitle(""));
                      }}
                    >
                      {t("Cancel")}
                    </Button>
                  </Box>
                </Box>
              </ClickAwayListener>
            );
          }
        }

        // Check if this status is being edited
        const isEditing = editingStatusId === params.id;

        // Regular row rendering
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              width: "100%",
              cursor:
                groupBy === "Status" && !isMyTaskView ? "pointer" : "default",
              height: "100%",
              py: 1,
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (!isEditing && groupBy === "Status" && !isMyTaskView) {
                handleTitleClick(params.id as string, params.value);
              }
            }}
          >
            {isEditing ? (
              <ClickAwayListener onClickAway={handleRenameSubmit}>
                <TextField
                  size="small"
                  value={editedStatusTitle}
                  onChange={handleRenameChange}
                  autoFocus
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: (theme) => theme.palette.background.paper,
                    },
                    "& .MuiInputBase-input": {
                      px: 1,
                      py: 0.5,
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                  }}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    handleRenameKeyPress(e);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  inputProps={{
                    style: { fontSize: "0.875rem", fontWeight: 500 },
                  }}
                />
              </ClickAwayListener>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  height: "100%",
                }}
              >
                {!getLoadingStatus() && params?.row?.icon && (
                  <Box
                    sx={{
                      bgcolor:
                        groupBy === "Status" ? params.row.color : "transparent",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50%",
                      p: 0.2,
                    }}
                  >
                    <Icon
                      icon={params.row.icon}
                      width={20}
                      height={20}
                      style={{
                        color:
                          groupBy === "Status"
                            ? "text.secondary"
                            : params.row.color || "#666",
                      }}
                    />
                  </Box>
                )}
                <Typography
                  variant="body2"
                  color="primary"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    "&:hover .edit-icon": {
                      opacity: 1,
                    },
                  }}
                >
                  {params.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ({params?.row?.totalCount || 0})
                </Typography>
              </Box>
            )}
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "",
      flex: 0.4,
      sortable: false,
      filterable: false,
      resizable: false,
      renderCell: (params) => {
        // Don't show actions for special rows or in My Tasks view
        if (
          params.id === ADD_STATUS_ROW_ID ||
          params.id === START_SPRINT_ROW_ID ||
          isMyTaskView
        ) {
          return null;
        }

        const actionList: AnyObject[] = [
          {
            label: t("Rename"),
            value: "rename",
            disabled: !DEFEKT_TRACKER.RENAME(myPermissions),
            hidden: DEFEKT_TRACKER.HIDE_RENAME(myPermissions),
          },
          {
            label: t("Delete"),
            value: "delete",
            disabled: !DEFEKT_TRACKER.DELETE(myPermissions),
            hidden: DEFEKT_TRACKER.HIDE_DELETE(myPermissions),
          },
        ];
        return (
          <Box pt={1}>
            <TableAction
              actionList={actionList}
              dataItem={params?.row}
              onAction={(data, action) => handleAction(data, action, "parent")}
              disabled={groupBy !== "Status"}
            />
          </Box>
        );
      },
    },
  ];

  const getRowsAsGrouped = (column: any) => {
    if (groupBy === "Status") {
      if (selectedStatuses.length > 0) {
        return selectedStatuses.includes(column?.id);
      } else {
        return true;
      }
    } else if (groupBy === "Assignee") {
      if (selectedAvatars.length > 0) {
        return selectedAvatars.includes(column?.id);
      } else {
        return (
          column?.tasks?.length > 0 ||
          loadingStates.loadingStatuses?.[column?.id]?.isInitialLoading
        );
      }
    } else if (groupBy === "Priority") {
      if (selectedPriorities.length > 0) {
        return selectedPriorities.includes(column?.title);
      } else {
        return true;
      }
    } else if (groupBy === "Tags") {
      if (selectedTags.length > 0) {
        return selectedTags.includes(column?.title);
      } else {
        return true;
      }
    } else {
      return true;
    }
  };

  // Create a rows array with all normal rows plus special rows
  const allRows = [];

  // Add the "Start Sprint" row at the top if conditions are met
  if (boardType === "SCRUM" && activeSprintIds?.length === 0) {
    allRows.push({
      id: START_SPRINT_ROW_ID,
      title:
        "Get started in backlog. Plan and start a sprint to see work here.",
      tasks: [],
    });
  }

  // Add normal rows
  const normalRows = columnOrder
    .map((columnId) => {
      const column = columns?.[columnId];
      return column;
    })
    .filter((column) => {
      return column && !column?.hidden && getRowsAsGrouped(column);
    });

  allRows.push(...normalRows);

  // Only add the "Add Status" row if groupBy is "Status" and not in My Tasks view
  if (groupBy === "Status" && !isMyTaskView) {
    allRows.push({ id: ADD_STATUS_ROW_ID, title: "", tasks: [] });
  }

  const getLoadingStatus = () => {
    const keys = Object.keys(loadingStates.loadingStatuses);
    let isLoading =
      isDeletingStatus ||
      isUpdatingStatus ||
      isCreatingStatus ||
      isInitialLoading;
    // keys?.forEach((key) => {
    //   if (
    //     loadingStates.loadingStatuses?.[key]?.isInitialLoading ||
    //     loadingStates.loadingStatuses?.[key]?.isLoadingMore
    //   ) {
    //     isLoading = true;
    //   }
    // });
    return isLoading;
  };

  return (
    <Box
      sx={{
        height: hasFilters ? "calc(100vh - 230px)" : "calc(100vh - 190px)",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <DataGridPro
        getRowId={(row) => row?.id}
        columns={listColumns}
        rows={allRows}
        loading={getLoadingStatus()}
        // rowHeight={45}
        rowSelection={false}
        localeText={customLocaleText}
        getDetailPanelHeight={() => "auto"}
        isRowExpandable={({ id }) => shouldShowDetailPanel(id)}
        getDetailPanelContent={({ row }) =>
          shouldShowDetailPanel(row.id) ? (
            <Box
              sx={{
                height: "100%",
                maxHeight: "100%",
                width: "100%",
                overflow: "hidden",
                position: "relative",
              }}
            >
              <NestedTaskList
                currentRow={row}
                handleAction={handleAction}
                type="task"
                pagination={pagination}
                handlePageChange={handlePageChange}
                loadingStatuses={loadingStates.loadingStatuses}
                projectDetails={projectDetails}
              />
            </Box>
          ) : null
        }
        pagination
        slots={{
          pagination: () => (
            <Pagination
              total={0}
              paginationModel={paginationModel}
              setPaginationModel={setPaginationModel}
            />
          ),
          noRowsOverlay: () => (
            <GridOverlay>
              <div>{t("No Data Found")}</div>
            </GridOverlay>
          ),
          detailPanelExpandIcon: KeyboardArrowRight,
          detailPanelCollapseIcon: KeyboardArrowDown,
          columnHeaders: () => null,
        }}
      />

      {/* Delete Confirmation Modal */}
      {isDeleteConfirmOpen && (
        <DeleteConfirmModal
          open={isDeleteConfirmOpen}
          onClose={handleDeleteConfirmClose}
          onConfirm={handleDeleteStatus}
          confirmMessage={t(
            `Are you sure you want to delete the status "${columnToDelete?.title}"? This will also delete all tasks in this status.`
          )}
          loading={isDeletingStatus}
          title={t("Delete Status")}
          okText={t("Delete")}
        />
      )}
    </Box>
  );
};

export default ListView;
